/** Minimal Graphen-native marks — no emoji or pictogram icons */

type BadgeSize = "sm" | "md" | "lg";

const badgeSizeClass: Record<BadgeSize, string> = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-14 w-14 text-base lg:h-16 lg:w-16 lg:text-lg",
};

interface HealthStageNumberBadgeProps {
    label: string;
    size?: BadgeSize;
    active?: boolean;
    className?: string;
}

export function HealthStageNumberBadge({
    label,
    size = "md",
    active = false,
    className = "",
}: HealthStageNumberBadgeProps) {
    return (
        <span
            className={`inline-flex shrink-0 items-center justify-center rounded-full border-2 font-bold leading-none ${badgeSizeClass[size]} ${
                active
                    ? "border-teal-500 bg-teal-500 text-white"
                    : "border-teal-300 bg-white text-teal-500"
            } ${className}`}
            aria-hidden="true"
        >
            {label}
        </span>
    );
}

interface HealthCoreMarkProps {
    className?: string;
    stroke?: string;
}

/** Hub mark — teal rounded tile + white reticle (original JSX paths) */
export function HealthCoreMark({
    className = "h-8 w-8",
    stroke = "white",
}: HealthCoreMarkProps) {
    const markStroke = stroke === "currentColor" ? "white" : stroke;

    return (
        <svg
            className={`block shrink-0 ${className}`}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <rect x="3" y="3" width="26" height="26" rx="7" fill="#037C84" />
            <circle
                cx="16"
                cy="16"
                r="9"
                stroke={markStroke}
                strokeWidth="1.25"
                opacity="0.45"
            />
            <circle
                cx="16"
                cy="16"
                r="5.5"
                stroke={markStroke}
                strokeWidth="1.25"
                opacity="0.7"
            />
            <circle cx="16" cy="16" r="2" fill={markStroke} />
            <path
                d="M16 7v3M16 22v3M7 16h3M22 16h3"
                stroke={markStroke}
                strokeWidth="1.25"
                strokeLinecap="round"
                opacity="0.6"
            />
        </svg>
    );
}

const STAGE_LINE_PATHS: Record<string, string> = {
    "home-health": "M6 14l5-5 5 5v8H6v-8z",
    "preventive-care": "M16 5l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V9l9-4z",
    "clinical-diagnosis": "M12 8h8v8h-8V8zm-2 10h12",
    "personalized-treatment": "M8 10h16v12H8V10zm4 4h8",
    "continuous-care": "M10 16c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6",
    diagnosis: "M12 8h8v8h-8V8zm-2 10h12",
    treatment: "M8 10h16v12H8V10zm4 4h8",
};

interface HealthStageLineMarkProps {
    stageId?: string;
    index?: number;
    active?: boolean;
    className?: string;
}

/** Minimal stroke icon per stage — line art only */
export function HealthStageLineMark({
    stageId,
    index = 0,
    active = false,
    className = "h-5 w-5 sm:h-6 sm:w-6",
}: HealthStageLineMarkProps) {
    const fallbackIds = [
        "home-health",
        "preventive-care",
        "clinical-diagnosis",
        "personalized-treatment",
        "continuous-care",
    ];
    const id = stageId && STAGE_LINE_PATHS[stageId] ? stageId : fallbackIds[index] ?? "home-health";
    const d = STAGE_LINE_PATHS[id] ?? STAGE_LINE_PATHS["home-health"];

    return (
        <svg
            className={`${className} ${active ? "text-teal-500" : "text-grey-400"}`}
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d={d}
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
